import * as Cesium from "cesium";

// 使用 BASE_URL，避免部署子路径时相对路径解析错误
const BASE = `${process.env.BASE_URL}baseData`;

/** 银川矢量底图数据（GeoJSON，WGS84，可直接用于 Cesium） */
export const BASE_LAYERS = {
  landuse: {
    name: "土地使用",
    url: `${BASE}/银川市土地使用_640100_GeoJSON/640100.geojson`,
    type: "polygon",
    colorHex: "#81c784",
    color: Cesium.Color.fromCssColorString("#81c784").withAlpha(0.25),
  },
  water: {
    name: "水域",
    url: `${BASE}/银川市水域_640100_GeoJSON/640100.geojson`,
    type: "polygon",
    colorHex: "#29b6f6",
    color: Cesium.Color.fromCssColorString("#29b6f6").withAlpha(0.45),
  },
  waterway: {
    name: "水系水路",
    url: `${BASE}/银川市水系水路_640100_GeoJSON/640100.geojson`,
    type: "polyline",
    colorHex: "#4fc3f7",
    color: Cesium.Color.fromCssColorString("#4fc3f7"),
    width: 3,
  },
  roads: {
    name: "道路路网",
    url: `${BASE}/银川市道路路网_640100_GeoJSON/640100.geojson`,
    type: "polyline",
    colorHex: "#ffcc66",
    color: Cesium.Color.fromCssColorString("#ffcc66"),
    width: 2,
    /** 道路全量约 8600 条，贴地成本极高；默认只画主干 */
    majorOnly: true,
    maxCount: 2000,
    majorClasses: [
      "motorway",
      "trunk",
      "primary",
      "secondary",
      "motorway_link",
      "trunk_link",
      "primary_link",
    ],
  },
  railway: {
    name: "铁路轨道",
    url: `${BASE}/银川市铁路轨道_640100_GeoJSON/640100.geojson`,
    type: "polyline",
    colorHex: "#ff6666",
    color: Cesium.Color.fromCssColorString("#ff6666"),
    width: 4,
  },
  buildings: {
    name: "建筑轮廓",
    url: `${BASE}/银川市建筑轮廓_640100_GeoJSON/640100.geojson`,
    type: "building",
    colorHex: "#90caf9",
    color: Cesium.Color.fromCssColorString("#90caf9").withAlpha(0.85),
    defaultHeight: 18,
  },
};

function styleEntity(entity, cfg) {
  if (cfg.type === "polyline" && entity.polyline) {
    entity.polyline.material = cfg.color;
    entity.polyline.width = cfg.width || 2;
    // 必须贴地：银川海拔 ~1100m，不贴地会画在椭球 0 高度被地形挡住
    entity.polyline.clampToGround = true;
  }

  if (cfg.type === "polygon" && entity.polygon) {
    entity.polygon.material = cfg.color;
    entity.polygon.outline = true;
    entity.polygon.outlineColor = Cesium.Color.WHITE.withAlpha(0.3);
    entity.polygon.height = undefined;
    entity.polygon.heightReference = Cesium.HeightReference.CLAMP_TO_GROUND;
  }

  if (cfg.type === "building" && entity.polygon) {
    entity.polygon.material = cfg.color;
    entity.polygon.outline = false;
    entity.polygon.extrudedHeight = cfg.defaultHeight || 18;
    entity.polygon.heightReference = Cesium.HeightReference.CLAMP_TO_GROUND;
    entity.polygon.extrudedHeightReference =
      Cesium.HeightReference.RELATIVE_TO_GROUND;
  }
}

/**
 * 道路：先 fetch + 过滤主干，再交给 Cesium
 */
async function loadRoadsFiltered(viewer, cfg) {
  const t0 = performance.now();
  const res = await fetch(cfg.url);
  const geo = await res.json();
  const allow = new Set(cfg.majorClasses || []);
  const maxCount = cfg.maxCount ?? 2000;
  const features = [];
  for (const f of geo.features || []) {
    const fc = f.properties?.fclass;
    if (fc && allow.has(fc)) {
      features.push(f);
      if (features.length >= maxCount) break;
    }
  }
  const filtered = { type: "FeatureCollection", features };
  console.log(
    `[baseData] 道路过滤: ${geo.features?.length ?? 0} → ${filtered.features.length} (${(performance.now() - t0).toFixed(0)}ms)`
  );

  const dataSource = await Cesium.GeoJsonDataSource.load(filtered, {
    clampToGround: true,
    stroke: cfg.color,
    strokeWidth: cfg.width || 2,
  });
  dataSource.name = cfg.name;
  viewer.dataSources.add(dataSource);
  for (const entity of dataSource.entities.values) {
    styleEntity(entity, cfg);
  }
  return dataSource;
}

/**
 * 加载一层 GeoJSON 到 viewer
 * @returns {Promise<Cesium.DataSource>}
 */
export async function loadGeoJsonLayer(viewer, layerKey) {
  const cfg = BASE_LAYERS[layerKey];
  if (!cfg) throw new Error(`未知图层: ${layerKey}`);

  if (cfg.type === "polyline" && cfg.majorOnly) {
    return loadRoadsFiltered(viewer, cfg);
  }

  const t0 = performance.now();
  const clamp = cfg.type === "polyline" || cfg.type === "polygon";
  const dataSource = await Cesium.GeoJsonDataSource.load(cfg.url, {
    clampToGround: clamp,
    stroke: cfg.color,
    fill: cfg.color,
    strokeWidth: cfg.width || 1,
  });
  dataSource.name = cfg.name;
  viewer.dataSources.add(dataSource);

  for (const entity of dataSource.entities.values) {
    styleEntity(entity, cfg);
  }
  console.log(
    `[baseData] ${cfg.name}: ${dataSource.entities.values.length} 实体 (${(performance.now() - t0).toFixed(0)}ms)`
  );
  return dataSource;
}

/**
 * 并行加载图层
 */
export async function loadBaseLayers(
  viewer,
  keys = ["roads", "water", "buildings"]
) {
  const settled = await Promise.allSettled(
    keys.map(async (key) => {
      const cfg = BASE_LAYERS[key];
      const dataSource = await loadGeoJsonLayer(viewer, key);
      console.log(`[baseData] 已加载: ${cfg.name}`);
      return {
        key,
        name: cfg.name,
        colorHex: cfg.colorHex,
        visible: true,
        dataSource,
      };
    })
  );

  const results = [];
  for (const s of settled) {
    if (s.status === "fulfilled") results.push(s.value);
    else console.error("[baseData] 加载失败:", s.reason);
  }
  return results;
}
